import { useCachedData } from './useCachedData';
import { supabase } from '../utils/supabase';
import { useToast } from '../context/ToastContext';
import { CACHE_KEYS } from '../constants/cacheKeys';
import { useAuth } from '../context/AuthContext';
import { CareerFocus, CareerFocusWithMetrics, calculateMetrics } from './useCareerFocus';

export interface FocusWithStatus extends CareerFocusWithMetrics {
  status: 'active' | 'inactive' | 'upcoming';
}

// Hook to get all career focuses (active and inactive)
export const useAllCareerFocuses = () => {
  const { showToast } = useToast();
  const { user } = useAuth();

  const cacheKey = user?.id ? `${CACHE_KEYS.CAREER_FOCUS_ALL}_${user.id}` : null;

  const { data: focuses, error, loading, refresh } = useCachedData<FocusWithStatus[]>(
    async () => {
      if (!user?.id) {
        return [];
      }

      const { data, error } = await supabase
        .from('user_career_focuses')
        .select('*')
        .eq('user_id', user.id)
        .order('start_date', { ascending: false });

      if (error) {
        console.error('[useAllCareerFocuses] Query error:', error);
        throw error;
      }

      // Calculate metrics and status for each focus
      const now = new Date();
      const focusesWithStatus = (data || []).map(focus => {
        const metricsData = calculateMetrics(focus);
        const startDate = new Date(focus.start_date);
        const endDate = new Date(focus.end_date);
        
        let status: 'active' | 'inactive' | 'upcoming';
        if (focus.is_active && now >= startDate && now <= endDate) {
          status = 'active';
        } else if (now < startDate) {
          status = 'upcoming';
        } else {
          status = 'inactive';
        }
        
        return {
          ...metricsData,
          status
        };
      });

      return focusesWithStatus;
    },
    {
      cacheKey: cacheKey || 'temp_all_career_focuses',
      enabled: !!user?.id && !!cacheKey,
      skipCache: !cacheKey,
      onError: (error) => {
        console.error('Failed to load career focuses:', error);
        showToast('Failed to load career focuses', 'error');
      }
    }
  );

  const activeFocuses = focuses?.filter(f => f.status === 'active') || [];
  const inactiveFocuses = focuses?.filter(f => f.status === 'inactive') || [];
  const upcomingFocuses = focuses?.filter(f => f.status === 'upcoming') || [];

  return { 
    focuses: focuses || [], 
    activeFocuses,
    inactiveFocuses,
    upcomingFocuses,
    error, 
    loading, 
    refresh 
  };
};